import { useMemo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import { allAbsencesOfStudent } from "./AbsencesOfStudentService";
import { useLocation, useNavigate } from "react-router-dom";
import { Calendar } from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persianFa from "react-date-object/locales/persian_fa";
import gregorian from "react-date-object/calendars/gregorian";
import "./AbsencesOfStudent.css";

const AbsencesOfStudent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { studentId } = location.state || {};

    const [isLoading, setIsLoading] = useState(true);
    const [absences, setAbsences] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        if (studentId) {
            getAllAbsencesOfStudent();
        } else {
            setIsLoading(false);
        }
    }, [studentId]);

    const getAllAbsencesOfStudent = async () => {
        try {
            setIsLoading(true);

            const data = await allAbsencesOfStudent(studentId);

            setAbsences(
                Array.isArray(data.result) ? data.result : []
            );
        } catch (err) {
            toast.error(
                err.response?.data?.message ||
                "خطا در دریافت لیست غیبت‌ها"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const absenceDates = useMemo(
        () =>
            new Set(
                absences
                    .map((absence) => {
                        const date = absence.date ?? absence.Date;
                        if (!date) return null;

                        return new DateObject({
                            date: String(date).slice(0, 10),
                            calendar: gregorian
                        })
                            .convert(persian, persianFa)
                            .format("YYYY/MM/DD");
                    })
                    .filter(Boolean)
            ),
        [absences]
    );

    const latestAbsenceDate = useMemo(
        () =>
            absences.reduce((latest, absence) => {
                const date = String(absence.date ?? absence.Date ?? "").slice(0, 10);
                return date > latest ? date : latest;
            }, ""),
        [absences]
    );

    const calendarStartDate = useMemo(
        () =>
            latestAbsenceDate
                ? new DateObject({
                    date: latestAbsenceDate,
                    calendar: gregorian
                }).convert(persian, persianFa)
                : undefined,
        [latestAbsenceDate]
    );

    const toPersianKey = (date) => date.format("YYYY/MM/DD");

    const selectedAbsence = selectedDate
        ? absenceDates.has(toPersianKey(selectedDate))
        : false;

    const persianDigits = [
        "۰", "۱", "۲", "۳", "۴",
        "۵", "۶", "۷", "۸", "۹"
    ];

    return (
        <>
            <style>{`
                .absence-page {
                    min-height: 100vh;
                    background: #f7f8fc;
                }

                .absence-container {
                    max-width: 720px;
                    margin: auto;
                }

                .student-absence-header {
                    height: 72px;
                    position: sticky;
                    top: 0;
                    z-index: 10000;
                    background: #726be7;
                    color: #fff;
                    border-bottom: 1px solid #e8edf4;
                    backdrop-filter: blur(12px);
                }

                .student-absence-header-inner {
                    max-width: 700px;
                    height: 100%;
                    margin: auto;
                    padding: 0 15px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 12px;
                }

                .student-absence-header-info {
                    min-width: 0;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .student-absence-header-icon,
                .student-absence-back-button {
                    width: 42px;
                    height: 42px;
                    flex-shrink: 0;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .student-absence-header-icon {
                    background: #eaf2ff;
                    color: #2563eb;
                }

                .student-absence-header-title {
                    margin: 0;
                    font-size: 15px;
                    line-height: 1.6;
                    font-weight: 800;
                    white-space: nowrap;
                }

                .student-absence-back-button {
                    border: 1px solid rgb(255 255 255 / 55%);
                    background: transparent;
                    color: #fff;
                }

                .student-absence-back-button svg {
                    transform: rotate(180deg);
                }

                .absence-header {
                    padding: 20px 4px 16px;
                }

                .absence-title {
                    font-size: 20px;
                    font-weight: 800;
                    color: #172033;
                    letter-spacing: -0.3px;
                }

                .absence-subtitle {
                    font-size: 13px;
                    color: #8992a3;
                    margin-top: 4px;
                }

                .summary-card {
                    position: relative;
                    overflow: hidden;
                    border: 0;
                    border-radius: 22px;
                    background: #ffffff;
                    box-shadow: 0 8px 30px rgba(25, 35, 55, 0.06);
                }

                .summary-card::after {
                    content: "";
                    position: absolute;
                    width: 130px;
                    height: 130px;
                    border-radius: 50%;
                    left: -55px;
                    top: -65px;
                    background: rgba(220, 53, 69, 0.06);
                }

                .summary-icon {
                    width: 52px;
                    height: 52px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #fff0f1;
                    color: #dc3545;
                    font-size: 21px;
                    flex-shrink: 0;
                }

                .summary-number {
                    font-size: 25px;
                    font-weight: 800;
                    line-height: 1;
                    color: #172033;
                }

                .summary-label {
                    font-size: 12px;
                    color: #8a93a4;
                    margin-top: 5px;
                }

                .section-title {
                    font-size: 14px;
                    font-weight: 800;
                    color: #30394b;
                    margin: 25px 4px 12px;
                }

                .absence-list {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }

                .absence-item {
                    background: #ffffff;
                    border-radius: 18px;
                    padding: 13px;
                    display: flex;
                    align-items: center;
                    box-shadow: 0 5px 20px rgba(25, 35, 55, 0.045);
                    border: 1px solid #f0f1f5;
                    transition: transform .15s ease, box-shadow .15s ease;
                }

                .absence-date {
                    width: 54px;
                    height: 54px;
                    border-radius: 15px;
                    background: #fff4f4;
                    color: #dc3545;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .absence-day {
                    font-size: 19px;
                    line-height: 20px;
                    font-weight: 800;
                }

                .absence-month {
                    font-size: 9px;
                    margin-top: 2px;
                    opacity: .8;
                }

                .absence-info {
                    flex: 1;
                    min-width: 0;
                    margin-right: 13px;
                }

                .absence-weekday {
                    font-size: 14px;
                    font-weight: 700;
                    color: #273044;
                    margin-bottom: 4px;
                }

                .absence-full-date {
                    font-size: 11px;
                    color: #929aaa;
                }

                .absence-status {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    padding: 6px 9px;
                    border-radius: 10px;
                    background: #fff4f4;
                    color: #dc3545;
                    font-size: 10px;
                    font-weight: 700;
                }

                .empty-card {
                    background: #ffffff;
                    border-radius: 22px;
                    padding: 48px 25px;
                    text-align: center;
                    box-shadow: 0 8px 30px rgba(25, 35, 55, 0.05);
                }

                .empty-icon {
                    width: 70px;
                    height: 70px;
                    border-radius: 22px;
                    margin: auto;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #effaf3;
                    color: #20a464;
                    font-size: 28px;
                }

                .empty-title {
                    font-size: 15px;
                    font-weight: 800;
                    color: #30394b;
                    margin-top: 17px;
                }

                .empty-text {
                    font-size: 12px;
                    color: #929aaa;
                    margin-top: 7px;
                }

                .loading-card {
                    background: #ffffff;
                    border-radius: 22px;
                    padding: 55px 20px;
                    text-align: center;
                    box-shadow: 0 8px 30px rgba(25, 35, 55, 0.05);
                }

                @media (max-width: 575.98px) {
                    .absence-page {
                        background: #f7f8fc;
                    }

                    .absence-container {
                        width: 100%;
                    }

                    .absence-header {
                        padding: 16px 2px 14px;
                    }

                    .absence-title {
                        font-size: 18px;
                    }

                    .student-absence-header-inner {
                        padding: 0 12px;
                    }

                    .student-absence-header-title {
                        font-size: 14px;
                    }

                    .summary-card {
                        border-radius: 19px;
                    }

                    .absence-item {
                        border-radius: 16px;
                        padding: 11px;
                    }

                    .absence-status {
                        width: 34px;
                        height: 34px;
                        padding: 0;
                        justify-content: center;
                    }

                    .absence-status span {
                        display: none;
                    }

                    .absence-date {
                        width: 50px;
                        height: 50px;
                    }
                }
            `}</style>

            <div
                className="absence-page"
                dir="rtl"
            >
                <header className="student-absence-header">
                    <div className="student-absence-header-inner">
                        <div className="student-absence-header-info">
                            <div className="student-absence-header-icon">
                                <svg
                                    width="21"
                                    height="21"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M6 4.5H18C19.1 4.5 20 5.4 20 6.5V19C20 20.1 19.1 21 18 21H6C4.9 21 4 20.1 4 19V6.5C4 5.4 4.9 4.5 6 4.5Z"
                                        stroke="currentColor"
                                        strokeWidth="1.7"
                                    />
                                    <path
                                        d="M8 3V6M16 3V6M4 9H20"
                                        stroke="currentColor"
                                        strokeWidth="1.7"
                                        strokeLinecap="round"
                                    />
                                    <path
                                        d="M8 13H8.01M12 13H12.01M16 13H16.01M8 17H8.01M12 17H12.01"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                </svg>
                            </div>
                            <h1 className="student-absence-header-title">
                                تاریخچه غیبت‌های دانش‌آموز
                            </h1>
                        </div>

                        <button
                            type="button"
                            className="student-absence-back-button"
                            onClick={() => navigate(-1)}
                            aria-label="بازگشت به فهرست دانش‌آموزان"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                aria-hidden="true"
                            >
                                <path
                                    d="M5 12H19M12 5L19 12L12 19"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                </header>

                <div>
                    <div className="absence-container">
                        {/* Loading */}
                        {isLoading && (
                            <div className="loading-card">
                                <div
                                    className="spinner-border text-primary"
                                    role="status"
                                >
                                    <span className="visually-hidden">
                                        در حال بارگذاری
                                    </span>
                                </div>

                                <div className="text-muted small mt-3">
                                    در حال دریافت اطلاعات...
                                </div>
                            </div>
                        )}

                        {!isLoading && (
                            <section className="absence-calendar-card" dir="rtl">


                                <div className="absence-calendar-wrapper">
                                    <Calendar
                                        calendar={persian}
                                        locale={persianFa}
                                        digits={persianDigits}
                                        value={selectedDate}
                                        currentDate={calendarStartDate}
                                        onChange={setSelectedDate}
                                        className="absence-calendar"
                                        mapDays={({ date }) => {
                                            if (!absenceDates.has(toPersianKey(date))) {
                                                return {};
                                            }

                                            return {
                                                className: "attendance-absent-day bg-danger",
                                                style: {
                                                    backgroundColor: "#dc3545",
                                                    color: "#ffffff",
                                                    borderRadius: "10px",
                                                    fontWeight: "bold"
                                                },
                                                "data-tooltip": `غیبت در تاریخ ${date.format("YYYY/MM/DD")}`,
                                                title: `غیبت در تاریخ ${date.format("YYYY/MM/DD")}`,
                                                "aria-label": `غیبت در تاریخ ${date.format("YYYY/MM/DD")}`
                                            };
                                        }}
                                    />
                                </div>

                                <p className="absence-calendar-count">
                                    تعداد غیبت: {absences.length}
                                </p>

                                {selectedDate && (
                                    <div className="absence-calendar-detail" aria-live="polite">
                                        <h3>جزئیات روز انتخاب‌شده</h3>
                                        <p>
                                            <strong>تاریخ شمسی:</strong>{" "}
                                            {selectedDate.format("dddd، DD MMMM YYYY")}
                                        </p>
                                        <p className={selectedAbsence ? "detail-absence" : "detail-normal"}>
                                            {selectedAbsence
                                                ? "وضعیت: غیبت دانش‌آموز"
                                                : "برای این روز غیبتی ثبت نشده است."}
                                        </p>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AbsencesOfStudent;